import * as z from "zod/v4";

/**
 * The only notable difference between Mongo and JSON is that mongoose supports dates.
 * This helper function makes it possible to support both sources in one schema.
 */
export const dateOrStringAsDate = z
  .union([z.date(), z.string()])
  .transform((value) => (typeof value === "string" ? new Date(value) : value));

export const numberlikeSchema = z.any().transform((value) => {
  const stringVal = String(value);
  return Number(stringVal);
}) as z.ZodType<number>;

/**
 * Converts Decimal128 or string to BigInt.
 * Used for large integers like IPv6 addresses (also works for IPv4).
 */
export const bigintlikeSchema = z.any().transform((value) => {
  const stringVal = String(value);
  return BigInt(stringVal);
}) as z.ZodType<bigint>;

/**
 * Type guard to check if value is a BSON Binary object.
 * BSON Binary has a .buffer property that is a Uint8Array.
 */
export function isBsonBinary(
  value: unknown,
): value is { buffer: Uint8Array | ArrayBuffer } {
  return (
    value !== null &&
    typeof value === "object" &&
    "buffer" in value &&
    (value.buffer instanceof Uint8Array || value.buffer instanceof ArrayBuffer)
  );
}

/**
 * Converts Buffer (stored as BinData in MongoDB) to BigInt.
 * Assumes big-endian byte order for proper lexicographic sorting.
 * Handles both Node.js Buffer and BSON Binary types.
 */
export const bufferToBigInt = z.any().transform((value) => {
  if (Buffer.isBuffer(value)) {
    return BigInt("0x" + value.toString("hex"));
  }
  if (isBsonBinary(value)) {
    // Convert to Uint8Array first to satisfy TypeScript's Buffer.from overload resolution
    const uint8Array =
      value.buffer instanceof Uint8Array
        ? value.buffer
        : new Uint8Array(value.buffer);
    return BigInt("0x" + Buffer.from(uint8Array).toString("hex"));
  }
  return BigInt(value);
});

/**
 * Validates YYYY-MM-DD date format.
 */
export const yyyyMmDdSchema = z.templateLiteral([
  z.number().min(1000).max(9999), // YYYY
  z.literal("-"),
  z.number().min(1).max(12), // MM
  z.literal("-"),
  z.number().min(1).max(31), // DD
]);
export type YyyyMmDd = z.infer<typeof yyyyMmDdSchema>;

/**
 * Validates ISO 8601 datetime format: YYYY-MM-DDTHH:mm:ss
 * Used for timezone-agnostic datetime strings that will be interpreted in specific timezones.
 */
export const iso8601DateTimeSchema = z.templateLiteral([
  z.number().min(1000).max(9999), // YYYY
  z.literal("-"),
  z.number().min(1).max(12), // MM
  z.literal("-"),
  z.number().min(1).max(31), // DD
  z.literal("T"),
  z.number().min(0).max(23), // HH
  z.literal(":"),
  z.number().min(0).max(59), // mm
  z.literal(":"),
  z.number().min(0).max(59), // ss
]);
export type Iso8601DateTime = z.infer<typeof iso8601DateTimeSchema>;
